import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useEffectiveAuth } from './GhostContext';
import LoadingScreen from '../components/shared/LoadingScreen';

interface TeacherPermissionsContextValue {
  permissions: Set<string>;
  roles: { id: string; name: string; color: string }[];
  loading: boolean;
}

const TeacherPermissionsContext = createContext<TeacherPermissionsContextValue>({
  permissions: new Set(),
  roles: [],
  loading: true,
});

export function TeacherPermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useEffectiveAuth();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<{ id: string; name: string; color: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchPermissions() {
      if (!user?.id) {
        if (!cancelled) { setPermissions(new Set()); setRoles([]); setLoading(false); }
        return;
      }
      setLoading(true);

      const { data: memberRows, error: memberErr } = await supabase
        .from('staff_role_members')
        .select('role_id')
        .eq('teacher_id', user.id);

      if (memberErr) console.error('[TeacherPermissions] failed to load role memberships', memberErr);

      const roleIds = (memberRows || []).map((r: any) => r.role_id);
      if (roleIds.length === 0) {
        if (!cancelled) { setPermissions(new Set()); setRoles([]); setLoading(false); }
        return;
      }

      const [roleResult, permResult] = await Promise.all([
        supabase.from('staff_roles').select('id, name, color').in('id', roleIds),
        supabase.from('staff_role_permissions').select('permission_key, enabled').in('role_id', roleIds).eq('enabled', true),
      ]);

      if (roleResult.error) console.error('[TeacherPermissions] failed to load role names', roleResult.error);
      if (!cancelled) setRoles(roleResult.data || []);

      if (permResult.error) console.error('[TeacherPermissions] failed to load permissions', permResult.error);
      if (cancelled) return;

      setPermissions(new Set((permResult.data || []).map((p: any) => p.permission_key)));
      setLoading(false);
    }

    fetchPermissions();
    return () => { cancelled = true; };
  }, [user?.id]);

  const value = useMemo(
    () => ({ permissions, roles, loading }),
    [permissions, roles, loading]
  );

  return (
    <TeacherPermissionsContext.Provider value={value}>
      {children}
    </TeacherPermissionsContext.Provider>
  );
}

export function useTeacherPermissions() {
  return useContext(TeacherPermissionsContext);
}

export function RequireTeacherPermission({ permissionKey, children }: { permissionKey: string; children: React.ReactNode }) {
  const { permissions, loading } = useTeacherPermissions();
  if (loading) return <LoadingScreen />;
  if (!permissions.has(permissionKey)) return <Navigate to="/teacher" replace />;
  return <>{children}</>;
}